import { useEffect, useRef, type ReactNode } from 'react';

import usePlaygroundDemoChromeVisible from './usePlaygroundDemoChromeVisible';

/**
 * The sticky header group of root.tsx's default shell (banner, Playground role switcher, nav),
 * which sticks as one unit (review, PR #2577). While QA Playground is ON it publishes its own
 * height as the `--app-chrome-height` custom property on <html>, so the walkthrough rail
 * (#9141) can stick directly beneath it. The group's height changes when Playground turns on
 * or off, and on wrap at narrow widths, so it is measured live rather than hard-coded.
 *
 * Outside Playground nothing reads that property, so no observer is mounted and nothing is
 * written: normal sessions get no global side effect from this Playground-only feature
 * (PR !2718 review, fp:stickychrome-observer-outside-playground).
 */
const StickyAppChrome = ({ children }: { children: ReactNode }) => {
  const ref = useRef<HTMLDivElement>(null);
  const railVisible = usePlaygroundDemoChromeVisible();

  useEffect(() => {
    const element = ref.current;
    if (!railVisible || !element || typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const root = document.documentElement;
    const publish = () =>
      root.style.setProperty('--app-chrome-height', `${element.offsetHeight}px`);
    publish();
    const observer = new ResizeObserver(publish);
    observer.observe(element);
    return () => {
      observer.disconnect();
      root.style.removeProperty('--app-chrome-height');
    };
  }, [railVisible]);

  return (
    <div ref={ref} className="sticky top-0 z-50">
      {children}
    </div>
  );
};

export default StickyAppChrome;
